/**
 * People removed from the hub whose Google or Microsoft accounts may not rejoin (by invite or
 * join rule) until an admin allows them again. Allowing keeps the record, so a recycled
 * email address never inherits the old login.
 */
import { UserX } from '@orgus/ui/icons';
import { useState } from 'react';
import { ROLE_RANK } from '@orgus/protocol';
import type { RemovedPerson, Role } from '@orgus/protocol';
import { Button, ConfirmDialog, DataTable, EmptyState, Panel, RelativeTime, StatusPill, Tooltip, toast } from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { authClient } from './client.ts';
import { ROLE_LABEL, ROLE_VALUE_CLASS, providerLabel } from './labels.ts';
import { ProviderMark } from './ProviderMark.tsx';

/** Allowed again is the quiet case (may rejoin): grey, never success green inside a removed list. */
const removedStatus = (r: RemovedPerson) => ({ status: r.blocked ? ('failed' as const) : ('ended' as const), label: r.blocked ? 'Blocked' : 'Allowed' });

export function RemovedPanel({ removed, myRole, onChanged }: { removed: RemovedPerson[]; myRole: Role; onChanged: () => void }) {
  const [allowing, setAllowing] = useState<RemovedPerson | null>(null);
  const blocked = removed.filter((r) => r.blocked).length;
  const columns: Column<RemovedPerson>[] = [
    {
      key: 'who',
      header: 'Person',
      grow: true,
      text: (r) => `${r.login} ${r.email ?? ''}`,
      sortValue: (r) => r.login,
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="flex shrink-0 items-center gap-1 text-fg-subtle" aria-label={r.providers.map(providerLabel).join(' and ')}>
            {r.providers.map((p) => (
              <ProviderMark key={p} id={p} className="size-3.5" />
            ))}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-fg">{r.email ?? r.login}</span>
            <span className="flex min-w-0 items-center gap-1 text-caption text-fg-subtle">
              {r.email && r.email !== r.login && <span className="truncate">{r.login}</span>}
              {/* Below 768 px the Status column folds into the meta line. */}
              <span className="shrink-0 md:hidden">
                <StatusPill variant="plain" {...removedStatus(r)} />
              </span>
            </span>
          </span>
        </span>
      ),
    },
    { key: 'role', header: 'Was', type: 'badge', hideBelow: 'md', sortValue: (r) => ROLE_RANK[r.role], cell: (r) => <span className={ROLE_VALUE_CLASS}>{ROLE_LABEL[r.role]}</span> },
    {
      key: 'removed',
      header: 'Removed',
      type: 'date',
      hideBelow: 'sm',
      sortValue: (r) => r.removedAt,
      cell: (r) => (
        <span className="text-sm text-fg-subtle">
          {r.removedBy}, <RelativeTime ts={r.removedAt} />
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      type: 'badge',
      hideBelow: 'md',
      sortValue: (r) => (r.blocked ? 0 : 1),
      cell: (r) => <StatusPill variant="plain" {...removedStatus(r)} />,
    },
  ];
  return (
    <Panel title="Removed people" sub={removed.length ? `${removed.length}${blocked ? ` · ${blocked} blocked` : ''}` : undefined} icon={<UserX />} flush>
      <DataTable
        frameless
        label="Removed people"
        rows={removed}
        rowKey={(r) => r.id}
        columns={columns}
        pageSize={false}
        initialSort={{ key: 'removed', dir: 'desc' }}
        empty={<EmptyState icon={<UserX />} title="Nobody removed" description="When you remove someone who signs in with Google or Microsoft, their accounts are blocked from rejoining here." />}
        rowActions={(r) => {
          if (!r.blocked) return null;
          const ownerOnly = r.role === 'owner' && myRole !== 'owner';
          return (
            <Tooltip content="Only an owner can allow a removed owner again." enabled={ownerOnly}>
              <span tabIndex={ownerOnly ? 0 : undefined}>
                <Button size="sm" variant="ghost" disabled={ownerOnly} onClick={() => setAllowing(r)}>
                  Allow again
                </Button>
              </span>
            </Tooltip>
          );
        }}
      />
      <ConfirmDialog
        open={!!allowing}
        onOpenChange={(v) => !v && setAllowing(null)}
        tone="primary"
        title={allowing ? `Allow ${allowing.email ?? allowing.login} again?` : ''}
        description="They can rejoin through a new invite or a join rule."
        confirmLabel="Allow again"
        onConfirm={async () => {
          if (!allowing) return;
          await authClient.allowRemoved(allowing.id);
          toast.success(`${allowing.email ?? allowing.login} may rejoin`);
          setAllowing(null);
          onChanged();
        }}
      />
    </Panel>
  );
}
